import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { asyncPoolAll, loadPaginatedFrontend, useFeathers } from '../../app/util';
import { District } from '../../app/feathers/districts/District';
import { FilterButton, SearchTextField } from '../';
import { DistrictIssues } from '../DistrictIssues';
import { HybridMapTreeIcon } from '../HybridMapTreeIcon';
import { Map, PeopleAlt, ArrowUpward, AccountTree, DeleteOutline } from '@mui/icons-material';
import { Button, Checkbox, CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';

const PAGE_SIZE = 50;

const tier2IssueTypes = [
  {
    Icon: Map,
    key: 'coverage-incomplete',
    tooltips: [
      'This district has a geoboundary.',
      'This district is missing a geoboundary.',
    ],
    columnTitle: 'Shapefile'
  },
  {
    Icon: HybridMapTreeIcon,
    key: 'children-coverage-incomplete',
    tooltips: [
      'This district has geoboundaries for all child districts.',
      'This district is missing geoboundaries for one or more child districts.',
    ],
    columnTitle: 'Child Geo'
  },
  {
    Icon: AccountTree,
    key: 'children-defined-incomplete',
    tooltips: [
      'This district has child districts configured.',
      'This district is missing child district configuration.',
    ],
    columnTitle: 'Child Districts'
  },
  {
    Icon: PeopleAlt,
    key: 'population-incomplete',
    tooltips: [
      'This district has population data.',
      'This district is missing population data!',
    ],
    columnTitle: 'Population'
  },
];

const parseBooleanFilter = (value?: string | null) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

interface DistrictsTableWithFiltersProps {
  parentId?: string;
  type?: string;
  stateMatchName?: string;
  baseQuery?: Record<string, any>;
  filterParams: Record<string, string | undefined | null>;
  onSetFilterParams: (updates: Record<string, string | null>) => void;
  defaultSort?: string;
  allowBulkDelete?: boolean;
  allowBulkDeleteGeodistrict?: boolean;
  disableSearchShortcut?: boolean;
  onDistrictsLoaded?: (districts: District[]) => void;
  onAfterBulkGeodistrictDelete?: () => void | Promise<void>;
  refreshKey?: number;
}

export const DistrictsTableWithFilters = ({
  parentId,
  type,
  stateMatchName,
  baseQuery = {},
  filterParams,
  onSetFilterParams,
  defaultSort = 'name:asc',
  allowBulkDelete = true,
  allowBulkDeleteGeodistrict = false,
  disableSearchShortcut = false,
  onDistrictsLoaded,
  onAfterBulkGeodistrictDelete,
  refreshKey = 0,
}: DistrictsTableWithFiltersProps) => {
  const feathers = useFeathers<Application<ServiceTypes>>();

  const [districts, setDistricts] = useState<District[]>([]);
  const [totalDistricts, setTotalDistricts] = useState(0);
  const [allDistrictsTotal, setAllDistrictsTotal] = useState(0);
  const [districtIssues, setDistrictIssues] = useState<Record<string, string[]>>({});
  const [bulkActionSelected, setBulkActionSelected] = useState<string[]>([]);
  const [selectAllMatchingQuery, setSelectAllMatchingQuery] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingError, setSavingError] = useState<string | null>(null);
  const [blockingRaces, setBlockingRaces] = useState<any[]>([]);
  const [blockingMeasures, setBlockingMeasures] = useState<any[]>([]);
  const [stateConfiguration, setStateConfiguration] = useState<StateConfiguration | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const page = Number(filterParams.page || '0');
  const sortKey = filterParams.sort || defaultSort;
  const searchTerm = filterParams.search || '';
  const bulkActionsEnabled = allowBulkDelete || allowBulkDeleteGeodistrict;
  const shapefileFilter = parseBooleanFilter(filterParams.shapefile);
  const childrenBoundariesFilter = parseBooleanFilter(filterParams.childrenBoundaries);
  const childrenDistrictsFilter = parseBooleanFilter(filterParams.childrenDistricts);
  const populationFilter = parseBooleanFilter(filterParams.population);
  const hasActiveFilters = shapefileFilter !== null
    || childrenBoundariesFilter !== null
    || childrenDistrictsFilter !== null
    || populationFilter !== null
    || searchTerm.trim().length > 0;
  const baseQueryKey = JSON.stringify(baseQuery);

  const [sortField, sortDirection] = useMemo(() => {
    const [field, direction] = sortKey.split(':');
    return [field || 'name', direction === 'desc' ? 'desc' : 'asc'] as const;
  }, [sortKey]);

  const activeDistrictQuery = useMemo(() => {
    const query: Record<string, any> = {
      ...baseQuery,
    };
    if (parentId) query.parentId = parentId;
    if (type) query.type = type;
    if (shapefileFilter !== null) {
      query.hasGeodistrict = shapefileFilter;
    }
    if (childrenBoundariesFilter === true) {
      query.childrenHaveGeodistrict = true;
    } else if (childrenBoundariesFilter === false) {
      query.childrenHaveGeodistrict = false;
    }
    if (populationFilter === true) {
      query.population = { $gt: 0 };
    } else if (populationFilter === false) {
      query.$or = [
        { population: { $exists: false } },
        { population: null },
        { population: 0 },
      ];
    }
    if (childrenDistrictsFilter === true) {
      query.childrenDefined = true;
    } else if (childrenDistrictsFilter === false) {
      query.childrenDefined = false;
    }
    if (searchTerm.trim()) {
      query.searchTerm = { $search: searchTerm.trim() };
    }
    return query;
  }, [baseQueryKey, parentId, type, shapefileFilter, childrenBoundariesFilter, childrenDistrictsFilter, populationFilter, searchTerm]);

  const clearBulkSelection = () => {
    setBulkActionSelected([]);
    setSelectAllMatchingQuery(false);
  };

  const selectedDistrictsWithGeodistrictCount = useMemo(() => {
    const selectedIds = new Set(bulkActionSelected);
    return districts.filter(district => selectedIds.has(district._id) && district.hasGeodistrict === true).length;
  }, [bulkActionSelected, districts]);

  const iconColumnStyling = {
    width: 220,
    maxWidth: 220,
    pr: 4,
  };

  const loadDistrictMetadata = async () => {
    if (!feathers) return;
    const districtService = feathers.getService('districts');
    const query: Record<string, any> = {
      ...baseQuery,
      $limit: 0,
    };
    if (parentId) query.parentId = parentId;
    if (type) query.type = type;
    const { total } = await districtService.find({
      query,
    });
    setAllDistrictsTotal(total || 0);
  };

  const loadDistrictPage = async () => {
    if (!feathers) return;
    const districtService = feathers.getService('districts');
    const query: Record<string, any> = {
      ...activeDistrictQuery,
      $skip: page * PAGE_SIZE,
      $limit: PAGE_SIZE,
      $sort: {
        [sortField]: sortDirection === 'desc' ? -1 : 1,
      },
    };
    const { data, total } = await districtService.find({ query });
    const nextDistricts = data || [];
    setDistricts(nextDistricts);
    setTotalDistricts(total || 0);
    onDistrictsLoaded?.(nextDistricts);

    const nextIssues = nextDistricts.reduce(
      (acc, next) => {
        acc[next._id] = [];
        if (!next.population) acc[next._id].push('population-incomplete');
        if (!next.hasGeodistrict) acc[next._id].push('coverage-incomplete');
        if (next.childrenHaveGeodistrict === false) acc[next._id].push('children-coverage-incomplete');
        if (!next.childrenDefined) acc[next._id].push('children-defined-incomplete');
        return acc;
      },
      {} as Record<string, string[]>,
    );
    setDistrictIssues(nextIssues);
  };

  const bulkDeleteDistricts = async () => {
    setSaving(true);
    setSavingError(null);
    setBlockingRaces([]);
    setBlockingMeasures([]);
    try {
      const districtIdsToDelete = selectAllMatchingQuery
        ? (await loadPaginatedFrontend<District>(
            'districts',
            {
              ...activeDistrictQuery,
              $select: ['_id'],
            },
            feathers,
            500,
          ) || []).map(district => district._id)
        : bulkActionSelected;

      await asyncPoolAll(5, districtIdsToDelete, (id: string) => feathers.getService('districts').remove(id));
      clearBulkSelection();
      await Promise.all([loadDistrictMetadata(), loadDistrictPage()]);
    } catch(err: any) {
      console.error(err);
      const data = err?.data || {};
      const backendMessage =
        err?.message ||
        data?.message ||
        data?.error ||
        err?.error?.message;
      setSavingError(backendMessage || 'Failed to delete districts');

      if (data?.races) {
        const active = Array.isArray(data.races.active) ? data.races.active : [];
        setBlockingRaces(active);
      }

      if (data?.measures) {
        const active = Array.isArray(data.measures.active) ? data.measures.active : [];
        setBlockingMeasures(active);
      }
    } finally {
      setSaving(false);
    }
  };

  const bulkDeleteGeodistricts = async () => {
    setSaving(true);
    setSavingError(null);
    setBlockingRaces([]);
    setBlockingMeasures([]);
    try {
      const districtIdsToDelete = selectAllMatchingQuery
        ? (await loadPaginatedFrontend<District>(
            'districts',
            {
              ...activeDistrictQuery,
              hasGeodistrict: true,
              $select: ['_id'],
            },
            feathers,
            500,
          ) || []).map(district => district._id)
        : districts
            .filter(district => bulkActionSelected.includes(district._id) && district.hasGeodistrict === true)
            .map(district => district._id);

      if (districtIdsToDelete.length === 0) {
        setSavingError('No selected districts have geodistricts to delete.');
        return;
      }

      if (!window.confirm(`Delete geodistricts for ${districtIdsToDelete.length} districts? District records will be kept.`)) {
        return;
      }

      await asyncPoolAll(5, districtIdsToDelete, (id: string) => feathers.getService('geo-districts').remove(id));
      clearBulkSelection();
      await Promise.all([loadDistrictMetadata(), loadDistrictPage(), onAfterBulkGeodistrictDelete?.()]);
    } catch(err: any) {
      console.error(err);
      const data = err?.data || {};
      const backendMessage =
        err?.message ||
        data?.message ||
        data?.error ||
        err?.error?.message;
      setSavingError(backendMessage || 'Failed to delete geodistricts');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!feathers) return;
    loadDistrictMetadata();
  }, [feathers, parentId, type, baseQueryKey, refreshKey]);

  useEffect(() => {
    if (!feathers) return;
    loadDistrictPage();
  }, [feathers, activeDistrictQuery, page, sortField, sortDirection, refreshKey]);

  useEffect(() => {
    if (!feathers || !stateMatchName) return;

    const stateCode = stateMatchName.split('-')[0];
    if (!stateCode) return;

    const loadStateConfiguration = async () => {
      try {
        const stateConfig = await feathers.getService('state-configurations').get(stateCode);
        setStateConfiguration(stateConfig);
      } catch (err) {
        console.error(err);
        setStateConfiguration(null);
      }
    };

    loadStateConfiguration();
  }, [feathers, stateMatchName]);

  const tierForType = useMemo(() => {
    const map: Record<string, number> = {};
    (stateConfiguration?.typeConfigurations || []).forEach(typeConfig => {
      if (typeConfig?.type && typeof typeConfig?.tierNumber === 'number') {
        map[typeConfig.type] = typeConfig.tierNumber;
      }
    });
    return map;
  }, [stateConfiguration]);

  const parentTypeByChildType = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(stateConfiguration?.typeStructure || {}).forEach(([parentType, childTypes]) => {
      (childTypes || []).forEach((childType) => {
        map[childType] = parentType;
      });
    });
    return map;
  }, [stateConfiguration]);

  const detailPathForDistrict = (district: District) => {
    if (!stateMatchName) return null;

    const districtTier = tierForType[district.type];
    if (districtTier === 1) {
      return `/states/${stateMatchName}`;
    }
    if (districtTier === 2 && district.matchName) {
      return `/states/${stateMatchName}/${district.type}/${district.matchName}`;
    }
    if (districtTier === 3 && district.parentId) {
      const parentType = parentTypeByChildType[district.type];
      return `/states/${stateMatchName}/${parentType}/${district.parentId}`;
    }
    return null;
  };

  return (
    <>
      <div style={{ margin: '28px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <FilterButton
              label="Boundary"
              value={shapefileFilter}
              onChange={(value) => {
                clearBulkSelection();
                onSetFilterParams({
                  shapefile: value === null ? null : `${value}`,
                  page: '0',
                });
              }}
              options={[
                { label: 'Has Boundary', value: true },
                { label: 'Missing Boundary', value: false }
              ]}
            />
            <FilterButton
              label="Children boundary"
              value={childrenBoundariesFilter}
              onChange={(value) => {
                clearBulkSelection();
                onSetFilterParams({
                  childrenBoundaries: value === null ? null : `${value}`,
                  page: '0',
                });
              }}
              options={[
                { label: 'Has Children Boundary', value: true },
                { label: 'Missing Children Boundary', value: false }
              ]}
            />
            <FilterButton
              label="Child districts"
              value={childrenDistrictsFilter}
              onChange={(value) => {
                clearBulkSelection();
                onSetFilterParams({
                  childrenDistricts: value === null ? null : `${value}`,
                  page: '0',
                });
              }}
              options={[
                { label: 'Has Child Districts', value: true },
                { label: 'Missing Child Districts', value: false }
              ]}
            />
            <FilterButton
              label="Population"
              value={populationFilter}
              onChange={(value) => {
                clearBulkSelection();
                onSetFilterParams({
                  population: value === null ? null : `${value}`,
                  page: '0',
                });
              }}
              options={[
                { label: 'Has Population', value: true },
                { label: 'Missing Population', value: false }
              ]}
            />
            <Typography variant="subtitle1">
          </Typography>
          </div>
          <div style={{ minWidth: 280, display: 'flex', justifyContent: 'flex-end' }}>
            <SearchTextField
              placeholder="Search"
              value={searchTerm}
              inputRef={searchInputRef}
              disableKeyboardShortcut={disableSearchShortcut}
              size="small"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                clearBulkSelection();
                onSetFilterParams({
                  search: event.target.value || null,
                  page: '0',
                });
              }}
            />
          </div>
        </div>
      </div>
      {
        savingError && (
          <Typography variant="body1" color="error">
            {savingError}
          </Typography>
        )
      }
      {
        (blockingRaces.length > 0 || blockingMeasures.length > 0) && (
          <div style={{ marginTop: 8, marginBottom: 16 }}>
            <Typography variant="body2" color="error" style={{ marginBottom: 4 }}>
              This district is referenced by the following items:
            </Typography>
            {blockingRaces.length > 0 && (
              <div style={{ marginBottom: 4 }}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Races
                </Typography>
                <ul style={{ marginTop: 2, paddingLeft: 20 }}>
                  {blockingRaces.map((race: any) => (
                    <li key={`race-${race._id}`}>
                      <Link to={`/elections/${race.election}/races/${race._id}`}>
                        {race.officeName || race.office || 'View race'}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {blockingMeasures.length > 0 && (
              <div>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Measures
                </Typography>
                <ul style={{ marginTop: 2, paddingLeft: 20 }}>
                  {blockingMeasures.map((measure: any) => (
                    <li key={`measure-${measure._id}`}>
                      <Link to={`/elections/${measure.election}/measures/${measure._id}`}>
                        {measure.title || 'View measure'}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )
      }
      <TableContainer component={Paper}>
        {
          bulkActionsEnabled && (selectAllMatchingQuery || bulkActionSelected.length > 0) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 16px', backgroundColor: '#EEE', gap: '12px', alignItems: 'center' }}>
              <Typography variant="body1">
                {selectAllMatchingQuery ? totalDistricts : bulkActionSelected.length} selected
              </Typography>
              <div>
                {allowBulkDeleteGeodistrict && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={bulkDeleteGeodistricts}
                    disabled={saving || (!selectAllMatchingQuery && selectedDistrictsWithGeodistrictCount === 0) || shapefileFilter === false}
                    endIcon={<Map color='error'/>}
                    style={{ marginRight: allowBulkDelete ? 8 : 0 }}
                  >
                    {saving ? <CircularProgress size={20} color="inherit" /> : 'Delete geodistrict'}
                  </Button>
                )}
                {allowBulkDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      const selectedCount = selectAllMatchingQuery ? totalDistricts : bulkActionSelected.length;
                      if (window.confirm(`Are you sure you want to delete ${selectedCount} districts?`)) {
                        bulkDeleteDistricts();
                      }
                    }}
                    disabled={saving}
                    endIcon={<DeleteOutline color='error'/>}
                  >
                    {saving ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                  </Button>
                )}
              </div>
            </div>
          )
        }
        <Table sx={{ minWidth: 650 }} aria-label="district table">
          <TableHead>
            <TableRow sx={{ '& th': { py: 0.75 } }}>
              {bulkActionsEnabled && (
                <TableCell>
                  <Checkbox
                    checked={selectAllMatchingQuery || (districts.length > 0 && bulkActionSelected.length === districts.length)}
                    indeterminate={!selectAllMatchingQuery && bulkActionSelected.length > 0 && bulkActionSelected.length < districts.length}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      if (checked) {
                        setSelectAllMatchingQuery(true);
                        setBulkActionSelected([]);
                      } else {
                        clearBulkSelection();
                      }
                    }}
                  />
                </TableCell>
              )}
              <TableCell style={{ width: '100%' }}>
                <SortedBy
                  label='name'
                  value={sortKey}
                  setSortKey={(value) => {
                    clearBulkSelection();
                    onSetFilterParams({ sort: value, page: '0' });
                  }}
                >
                  <Typography variant='body1'>
                    Name
                  </Typography>
                </SortedBy>
              </TableCell>
              <TableCell align="right">
                <SortedBy
                  label='population'
                  value={sortKey}
                  setSortKey={(value) => {
                    clearBulkSelection();
                    onSetFilterParams({ sort: value, page: '0' });
                  }}
                >
                  <Typography variant='body1'>
                    Population
                  </Typography>
                </SortedBy>
              </TableCell>
              <TableCell sx={iconColumnStyling} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {districts.map((district) => {
              const detailPath = detailPathForDistrict(district);
              const rowCells = (
                <>
                  {bulkActionsEnabled && (
                    <TableCell style={{ textAlign: 'center' }}>
                      <Checkbox
                        checked={selectAllMatchingQuery || bulkActionSelected.includes(district._id)}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (selectAllMatchingQuery) {
                            setSelectAllMatchingQuery(false);
                            setBulkActionSelected(districts.map(d => d._id).filter(id => id !== district._id));
                            return;
                          }
                          const checked = !bulkActionSelected.includes(district._id);

                          if (checked) {
                            setBulkActionSelected([...bulkActionSelected, district._id]);
                          } else {
                            setBulkActionSelected(bulkActionSelected.filter(id => id !== district._id));
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell component="th" scope="row">
                    <Typography variant='body1'>
                      {district.longName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant='body2' style={{ opacity: district.population ? 1 : 0.6 }}>
                      {district.population ? district.population.toLocaleString() : '--'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={iconColumnStyling}>
                    <DistrictIssues
                      issues={districtIssues[district._id]}
                      issueTypes={tier2IssueTypes}
                      style={{ justifyContent: 'flex-end' }}
                    />
                  </TableCell>
                </>
              );

              if (detailPath) {
                return (
                  <TableRow
                    key={district._id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    component={Link}
                    to={detailPath}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    {rowCells}
                  </TableRow>
                );
              }

              return (
                <TableRow
                  key={district._id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  {rowCells}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalDistricts}
          page={page}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_, nextPage) => {
            clearBulkSelection();
            onSetFilterParams({ page: `${nextPage}` });
          }}
        />
      </TableContainer>
    </>
  );
};

const SortedBy = ({
  label,
  value,
  setSortKey,
  children,
} : {
  label: string;
  value: string;
  setSortKey: (value: string) => void;
  children: React.ReactNode;
}) => {
  const sortedBy = value && value.startsWith(label);
  const ascending = value && value.endsWith('asc');
  const Icon = ascending
    ? <ArrowUpward style={{ fontSize: '16px' }} />
    : <ArrowUpward style={{ transform: 'rotate(180deg)', fontSize: '16px' }}/>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }} onClick={() => {
      if(sortedBy && ascending) {
        setSortKey(`${label}:desc`);
      } else {
        setSortKey(`${label}:asc`);
      }
    }}>
      {children}
      {
        sortedBy && Icon
      }
    </div>
  );
};

